@if (sec.isMobileScanningMode()) {
    <pcg-mobile-scanning></pcg-mobile-scanning>
} @else {
    @if (isLoading()) {
        <div class="page-spinner">
            <mat-spinner></mat-spinner>
        </div>
    }

    @if (isLoggedOn()) {
        <pcg-sse-notification/>
        <!-- Single app-shell mount for the tour engine overlay (ordering tutorial + issuance walkthrough). -->
        <pcg-tutorial-overlay />
        <!-- Global confirm dialog for the root ConfirmationService (issuance walkthrough start prompt). -->
        <p-confirmdialog [style]="{ width: '30rem', maxWidth: '95vw' }" />
    }
	
    <!-- Training-mode strip: sits above all nav/content and pushes the shell down (see .has-training-banner). -->
    @if (isLoggedOn() && !isLoading() && showTrainingBanner()) {
        <pcg-training-banner (completed)="onTrainingCompleted()" />
    }

    @if (!isLoading()){
        <mat-sidenav-container
            class="nav-container"
            [class.has-training-banner]="showTrainingBanner()"
            (backdropClick)="backdropClick()"
        >
            <!--#region Navigation menu -->
            @if (isLoggedOn()) {
                <mat-sidenav 
                    #sidenav 
                    [mode]="
                        navService.isNavFixed() 
                        && !navService.isMobile() ? 'side' : 'over'
                    "
                    [(opened)]="navService.isNavOpen"
                    (openedChange)="navService.toggleNavOpen($event)"
                    (mouseleave)="navService.toggleNavOpen(navService.isNavFixed())" 
                    class="no-print overflow-hidden"
                    autosize
                >
                    <pcg-mat-side-nav 
                        [user]="user" 
                        id="pcgSideNav" 
                        class="no-print"
                    ></pcg-mat-side-nav>
                </mat-sidenav>
            }
    
            @if (isLoggedOn()) {
                <mat-sidenav 
                    #rightSidenav 
                    position="end"
                    mode="over"
                    hasBackdrop="false" 
                    [opened]="navService.sideNavType() !== 'none'"
                    class='border-0 p-3 notification-width overflow-hidden'
                >
                    @if (navService.sideNavType() === 'notifications') {
                        <pcg-dashboard-notifications-widget 
                            [isSideNav]="true" 
                            [notificationType]="notificationType.Notification"
                        />
                    }
                    @if (navService.sideNavType() === 'messages') {
                        <pcg-dashboard-notifications-widget 
                            [isSideNav]="true" 
                            [notificationType]="notificationType.Chat"
                        />
                    }
                    @if (navService.sideNavType() === 'chatroom') {
                        <pcg-chatroom
                            [useCloseBtn]="true"
                            (closed)="navService.toggleChatroomNav()"
                            [isOpened]="false"
                        />
                    }
                </mat-sidenav>
            }    
            <!--#endregion-->
        
            <!--#region Rest of the site -->
            <mat-sidenav-content 
                class="overflow-hidden" 
                [class.darker]='isLoggedOn() && navService?.isNavOpen()'
            >
                <!--#region Nav drawer -->
                @if (
                    !navService.isMobile()
                    && !navService.isNavFixed()
                    && isLoggedOn()
                    && navService.navRoutes()
                ) {
                    <nav class="nav-drawer no-print scrollbar-hidden">
                        <div class="nav-drawer-image mb-2">
                            <button
                                class="d-flex align-items-center justify-content-center mt-2 fixed-toggle-btn"
                                data-tour="tour-open-nav"
                                (click)="navService.toggleNavFixed(true)"
                                ngbTooltip="Expand sidebar"
                            >
                                <fa-icon 
                                    [icon]="faIconName.faExpand" 
                                    class="fa-lg"
                                ></fa-icon>
                            </button>
                        </div>

                        <a 
                            class="drawer-icon pointer" 
                            [routerLink]="'/dashboard'"
                        >
                            <fa-icon 
                                [icon]="faIconName.faHouse" 
                                class="drawer-icon fa-sm"
                                [class.active-drawer-item]="navService.selectedMainNav() === 'dashboard'"
                            ></fa-icon>
                        </a>
            
                        @for (nav of navService.navRoutes(); track nav;) {
                            @if (
                                navService.canViewNav(nav)
                                && nav.icon
                            ) {
                                <a 
                                    class="drawer-icon pointer" 
                                    [routerLink]="nav.href ?? nav.path"
                                >
                                    <fa-icon 
                                        [icon]="nav.icon" 
                                        class="drawer-icon fa-sm"
                                        [class.active-drawer-item]="nav.name === navService.selectedMainNav()"
                                    ></fa-icon>
                                </a>
                            }
                        }
            
                        <a 
                            class="drawer-icon pointer" 
                            [routerLink]="userUrl()"
                        > 
                            <fa-icon 
                                [icon]="faIconName.faUser" 
                                class="drawer-icon pointer"
                                [class.active-drawer-item]='navService.selectedMainNav() === "profile"'
                            ></fa-icon>
                        </a>
    
                        <!-- Notifications -->
                        <div 
                            class="icon-container" 
                            (click)="navService.openNotificationsNav()"
                        >
                            <fa-icon 
                                [icon]="faIconName.faBell" 
                                class="drawer-icon fa-sm"
                            ></fa-icon>
                            @if (notificationsService.unreadNotificationsCount() > 0) { 
                                <span class="icon-badge badge-danger">{{ notificationsService.unreadNotificationsCount() }}</span> 
                            }
                        </div>
    
                        <!-- Messages -->
                        <div 
                            class="icon-container" 
                            (click)="navService.openMessagesNav()"
                        >
                            <fa-icon 
                                [icon]="faIconName.faMessages" 
                                class="drawer-icon fa-sm"
                            ></fa-icon>
                            @if (notificationsService.unreadMessagesCount() > 0) { 
                                <span class="icon-badge badge-info">{{ notificationsService.unreadMessagesCount() }}</span> 
                            }
                        </div>
                    </nav>
                }
                <!--#endregion-->
            
                <!--#region Everything else -->
                <div 
                    [ngClass]="{'footer-space': navService?.selectedMainNav() !== 'dashboard',
                        'dashboard-footer-space': navService?.selectedMainNav() === 'dashboard',
                        'left-shift-fixed': isLoggedOn() && navService.isNavFixed(),
                        'left-shift': isLoggedOn() && !navService.isNavFixed(),
                        'insulin-surface': isInsulinArea()}"
                >
                    @if (isLoggedOn()) {
                        <pcg-header 
                            id='mainTopHeader' 
                            class="main-header" 
                            (hamburgerClick)="navService.toggleNavOpen($event)"
                            [tabPanel]="tabPanel"
                        ></pcg-header>
                    }
    
                    <div 
                        [ngClass]="
                            (
                                !isLoggedOn() 
                                || navService?.selectedMainNav() === 'dashboard'
                            ) 
                            ? '' 
                            : navService.isNavFixed() 
                                ? 'top-shift-fixed' 
                                : 'top-shift'
                        "
                        class="mb-3 position-relative" 
                        [class.banner-shift]="gs.isBannerDisplayed()"
                        (mouseover)="navService.toggleNavOpen(false)"
                    >
                        <main class="main-container">
                            <mat-tab-nav-panel 
                                #tabPanel 
                                class="fix-sticky-table"
                                [class]="navService?.selectedMainNav() === 'dashboard' ? 'w-100' : 'container'"
                            >
                                <router-outlet></router-outlet>
                            </mat-tab-nav-panel>
                            <!-- @if(isLoggedOn()) {
                                <pcg-quick-user-info class="no-print" /> 
                            } -->
                        </main>
                        <!-- <pcg-spinner></pcg-spinner> Commented out for now just incase we got to go back-->
                    </div>
                </div>
                <!--#endregion-->
    
                <!--#region Footer -->
                <div 
                    [class.footer-nav-fixed]="isLoggedOn() && navService.isNavFixed()"
                    [class.insulin-surface]="isInsulinArea()"
                    class="footer-ctnr no-print"
                >
                    <div
                        class="d-flex justify-content-center"
                        [class]="isLoggedOn() && navService?.selectedMainNav() === 'dashboard' 
                            ? 'dashboard-footer' 
                            : 'footer'"
                        >
                        <span id="versionHistory">
                            <span>
                                System by
                                <a 
                                    target="_blank" 
                                    rel="noreferrer noopener" 
                                    href="http://www.paulconsultinggroup.com"
                                >Paul Consulting Group</a>
                            </span>
                            @if (isLoggedOn()) {
                                &nbsp;|&nbsp;
                                <a 
                                    onclick="$router.navigateByUrl('/support/web-release-notes'); return false;"
                                    href="support/web-release-notes"
                                >Release Notes</a>
                            }
                        </span>
                    </div>
                </div>
                <!--#endregion-->
            </mat-sidenav-content>
            <!--#endregion-->
        </mat-sidenav-container>
    }
}